/**
 * Gemini Vision OCR Service
 * Extracts structured data from physical OBIS admission form photos
 */

export interface ExtractedAdmissionData {
  studentName?: string;
  dateOfBirth?: string;       // YYYY-MM-DD format
  gender?: string;            // 'male' | 'female'
  religion?: string;
  classApplyingFor?: string;  // e.g. 'Class II'
  previousSchool?: string;
  fatherName?: string;
  motherName?: string;
  primaryPhone?: string;      // +91XXXXXXXXXX format
  alternatePhone?: string;
  email?: string;
  occupation?: string;
  address?: string;
  locality?: string;
  pinCode?: string;
  isNameCorrected?: boolean;
  isDobCorrected?: boolean;
  confidence: 'high' | 'medium' | 'low';
  rawText?: string;
  notes?: string;
}

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;

const DUAL_EXTRACTION_PROMPT = `You are an expert at reading handwritten school admission forms vs official birth certificates. 
Extract all available information from these two images:
1. One is an OBIS Admission Form (handwritten).
2. One is a Birth Certificate (official document).

CRITICAL RULES:
- Use the Birth Certificate as the DEFINITIVE source for the "studentName" spelling and "dateOfBirth".
- Use the Admission Form for parent names, phone numbers, address, and class applying for.
- Compare the two. If the name on the form is spelled differently than the certificate, set "isNameCorrected" to true.
- If the DOB on the form differs from the certificate, set "isDobCorrected" to true.
- Normalize all names (Student, Father, Mother) to Title Case (e.g. "MD HASIBUR" → "Md Hasibur").

Return a JSON object with these fields:
{
  "studentName": "official name from certificate",
  "dateOfBirth": "YYYY-MM-DD from certificate",
  "gender": "male or female",
  "religion": "from form",
  "classApplyingFor": "normalized e.g. 'Class II'",
  "previousSchool": "from form",
  "fatherName": "Title Case name",
  "motherName": "Title Case name",
  "primaryPhone": "10-digits",
  "alternatePhone": "10-digits",
  "email": "from form",
  "occupation": "from form",
  "address": "from form",
  "locality": "from form",
  "pinCode": "6-digits",
  "isNameCorrected": boolean,
  "isDobCorrected": boolean,
  "confidence": "high|medium|low"
}`;

const EXTRACTION_PROMPT = `You are an expert at reading handwritten school admission forms. 
Extract all available information from this image of an OBIS Admission Form.

RULES:
- Normalize all names to Title Case (e.g. "MD MOAZZAM" → "Md Moazzam").
- Use YYYY-MM-DD for dates.

Return a JSON object with these exact fields:
{
  "studentName": "Title Case name",
  "dateOfBirth": "YYYY-MM-DD",
  "gender": "male or female",
  "religion": "as written",
  "classApplyingFor": "normalized",
  "previousSchool": "as written",
  "fatherName": "Title Case name",
  "motherName": "Title Case name",
  "primaryPhone": "10-digits",
  "alternatePhone": "10-digits",
  "email": "email",
  "occupation": "occupation",
  "address": "address",
  "locality": "locality",
  "pinCode": "pinCode",
  "confidence": "high|medium|low"
}`;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g. "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
  return digits.length >= 10 ? `+91${digits.slice(-10)}` : '';
}

function normalizeClass(raw: string | null | undefined): string {
  if (!raw) return '';
  const val = raw.trim().toUpperCase();
  const map: Record<string, string> = {
    'PLAYGROUP': 'Nursery', 'PG': 'Nursery',
    'PS': 'Nursery',
    'NURSERY': 'Nursery', 'NUR': 'Nursery',
    'JR.KG': 'Jr.KG', 'JR KG': 'Jr.KG', 'JUNIOR KG': 'Jr.KG', 'LKG': 'Jr.KG',
    'SR.KG': 'Sr.KG', 'SR KG': 'Sr.KG', 'SENIOR KG': 'Sr.KG', 'UKG': 'Sr.KG',
    'I': 'Class I', 'CLASS I': 'Class I', '1': 'Class I',
    'II': 'Class II', 'CLASS II': 'Class II', '2': 'Class II',
    'III': 'Class III', 'CLASS III': 'Class III', '3': 'Class III',
    'IV': 'Class IV', 'CLASS IV': 'Class IV', '4': 'Class IV',
    'V': 'Class V', 'CLASS V': 'Class V', '5': 'Class V',
    'VI': 'Class VI', 'CLASS VI': 'Class VI', '6': 'Class VI',
    'VII': 'Class VII', 'CLASS VII': 'Class VII', '7': 'Class VII',
  };
  return map[val] || raw;
}

function toProperCase(str: string | null | undefined): string {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

export async function extractAdmissionFormData(formFile: File, certificateFile?: File): Promise<ExtractedAdmissionData> {
  if (!GEMINI_API_KEY) {
    throw new Error('Gemini API key not configured');
  }

  const prompt = certificateFile ? DUAL_EXTRACTION_PROMPT : EXTRACTION_PROMPT;
  const parts: any[] = [{ text: prompt }];

  // Add form image
  const formBase64 = await fileToBase64(formFile);
  parts.push({
    inlineData: { mimeType: formFile.type || 'image/jpeg', data: formBase64 }
  });

  // Add certificate image if provided
  if (certificateFile) {
    const certBase64 = await fileToBase64(certificateFile);
    parts.push({
      inlineData: { mimeType: certificateFile.type || 'image/jpeg', data: certBase64 }
    });
  }

  const requestBody = {
    contents: [{ parts }],
    generationConfig: {
      temperature: 0.1,
      maxOutputTokens: 8192,
      responseMimeType: 'application/json',
      responseSchema: {
        type: 'OBJECT',
        properties: {
          studentName: { type: 'STRING' },
          dateOfBirth: { type: 'STRING' },
          gender: { type: 'STRING' },
          religion: { type: 'STRING' },
          classApplyingFor: { type: 'STRING' },
          previousSchool: { type: 'STRING' },
          fatherName: { type: 'STRING' },
          motherName: { type: 'STRING' },
          primaryPhone: { type: 'STRING' },
          alternatePhone: { type: 'STRING' },
          email: { type: 'STRING' },
          occupation: { type: 'STRING' },
          address: { type: 'STRING' },
          locality: { type: 'STRING' },
          pinCode: { type: 'STRING' },
          isNameCorrected: { type: 'BOOLEAN' },
          isDobCorrected: { type: 'BOOLEAN' },
          confidence: { type: 'STRING' }
        },
        required: [
          'studentName', 'dateOfBirth', 'gender', 'religion',
          'classApplyingFor', 'previousSchool', 'fatherName',
          'motherName', 'primaryPhone', 'alternatePhone',
          'email', 'occupation', 'address', 'locality',
          'pinCode', 'isNameCorrected', 'isDobCorrected', 'confidence'
        ]
      }
    }
  };

  const response = await fetch(GEMINI_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const err = await response.text();
    throw new Error(`Gemini API error: ${response.status} - ${err}`);
  }

  const result = await response.json();
  const candidate = result.candidates?.[0];
  
  if (!candidate) {
    throw new Error('No candidate response from Gemini. Check safety filters.');
  }

  if (candidate.finishReason === 'SAFETY') {
    throw new Error('Scan blocked by safety filters. Please ensure documents are clearly visible and appropriate.');
  }

  const text = candidate.content?.parts?.[0]?.text;

  if (!text) {
    throw new Error('Empty response from Gemini');
  }

  // With Response Schema + JSON Mode, the response is guaranteed to be clean JSON
  let raw;
  try {
    raw = JSON.parse(text.trim());
  } catch (parseErr) {
    console.error('Gemini JSON Parse Error. Raw Text:', text);
    throw new Error(`JSON Parse error: ${parseErr.message}`);
  }

  // Normalize and clean extracted data
  return {
    studentName: toProperCase(raw.studentName),
    dateOfBirth: raw.dateOfBirth || '',
    gender: raw.gender?.toLowerCase() || '',
    religion: raw.religion || '',
    classApplyingFor: normalizeClass(raw.classApplyingFor),
    previousSchool: raw.previousSchool || '',
    fatherName: toProperCase(raw.fatherName),
    motherName: toProperCase(raw.motherName),
    primaryPhone: normalizePhone(raw.primaryPhone),
    alternatePhone: normalizePhone(raw.alternatePhone),
    email: raw.email || '',
    occupation: raw.occupation || '',
    address: raw.address || '',
    locality: raw.locality || '',
    pinCode: raw.pinCode || '',
    isNameCorrected: !!raw.isNameCorrected,
    isDobCorrected: !!raw.isDobCorrected,
    confidence: raw.confidence || 'medium',
    rawText: text,
  };
}
